import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { USER_ASSIGNMENT_STATUS } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.constants';
import {
  determineUserAssignmentStatus,
  updateUserAccess,
  updateAllUsersAccess,
} from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.utils';
import * as reportAccessService from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.helpers';

import type { ReportData, ReportUser } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-data';
import type { ReportUsersMap } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-users';

interface UserAccessChangePayload {
  userId: string | number;
  hasAccess: boolean;
}

interface UseAccessActionsResult {
  handleReportAccessChange: (
    reportId: string | number,
    value: boolean,
  ) => Promise<void>;
  handleDataIssueChange: (
    reportId: string | number,
    value: boolean,
  ) => Promise<void>;
  handleUserAccessChange: (
    reportId: string | number,
    userData: UserAccessChangePayload,
  ) => Promise<void>;
}

export const useAccessActions = (
  reports: ReportData[],
  setReports: Dispatch<SetStateAction<ReportData[]>>,
  reportUsers: ReportUsersMap,
  setReportUsers: Dispatch<SetStateAction<ReportUsersMap>>,
  userId: string | undefined,
  clientId: string | number | undefined,
  loading: boolean,
  setLoading: Dispatch<SetStateAction<boolean>>,
): UseAccessActionsResult => {
  const handleReportAccessChange = useCallback(
    async (reportId: string | number, value: boolean): Promise<void> => {
      if (loading) return;

      setLoading(true);
      try {
        const currentReport = reports.find((report) => report.id === reportId);

        if (!currentReport) return;

        const newStatus = value
          ? USER_ASSIGNMENT_STATUS.ALL_ASSIGNED
          : USER_ASSIGNMENT_STATUS.NONE_ASSIGNED;

        const { userAssignmentStatus } = (await reportAccessService.updateReportAccess({
          userId: userId ?? '',
          clientId: clientId ?? '',
          reportId,
          value: {
            userAssignmentStatus: newStatus,
            hasDataIssue: !!currentReport.hasDataIssue,
          },
        })) as { userAssignmentStatus?: string };

        setReports((prevReports) =>
          prevReports.map((report) =>
            report.id === reportId
              ? {
                  ...report,
                  userAssignmentStatus: userAssignmentStatus ?? newStatus,
                }
              : report,
          ),
        );

        if (reportUsers[reportId]) {
          setReportUsers((prev) => ({
            ...prev,
            [reportId]: updateAllUsersAccess(prev[reportId] || [], value),
          }));
        }
      } catch (error) {
        console.error('Error updating report access:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      reports,
      reportUsers,
      userId,
      clientId,
      loading,
      setLoading,
      setReports,
      setReportUsers,
    ],
  );

  const handleDataIssueChange = useCallback(
    async (reportId: string | number, value: boolean): Promise<void> => {
      if (loading) return;

      setLoading(true);
      try {
        const currentReport = reports.find((report) => report.id === reportId);

        if (!currentReport) return;

        await reportAccessService.updateReportAccess({
          userId: userId ?? '',
          clientId: clientId ?? '',
          reportId,
          value: {
            userAssignmentStatus: currentReport.userAssignmentStatus ?? '',
            hasDataIssue: value,
          },
        });

        setReports((prevReports) =>
          prevReports.map((report) =>
            report.id === reportId ? { ...report, hasDataIssue: value } : report,
          ),
        );
      } catch (error) {
        console.error('Error updating data issue flag:', error);
      } finally {
        setLoading(false);
      }
    },
    [reports, userId, clientId, loading, setLoading, setReports],
  );

  const handleUserAccessChange = useCallback(
    async (
      reportId: string | number,
      userData: UserAccessChangePayload,
    ): Promise<void> => {
      if (loading) return;

      setLoading(true);
      try {
        const currentReport = reports.find((report) => report.id === reportId);
        const currentUsers = reportUsers[reportId] || [];

        if (!currentReport) return;

        if (userData.hasAccess) {
          await reportAccessService.assignReportToUser({
            userId: userId ?? '',
            reportId,
            id: userData.userId,
          });
        } else {
          await reportAccessService.unassignReportFromUser({
            userId: userId ?? '',
            reportId,
            id: userData.userId,
          });
        }

        const updatedUsers = updateUserAccess(
          currentUsers,
          userData.userId,
          userData.hasAccess,
        ) as ReportUser[];

        const newStatus = determineUserAssignmentStatus(updatedUsers);

        if (currentReport.userAssignmentStatus !== newStatus) {
          const { userAssignmentStatus } = (await reportAccessService.updateReportAccess({
            userId: userId ?? '',
            clientId: clientId ?? '',
            reportId,
            value: {
              userAssignmentStatus: newStatus,
              hasDataIssue: !!currentReport.hasDataIssue,
            },
          })) as { userAssignmentStatus?: string };

          setReports((prevReports) =>
            prevReports.map((report) =>
              report.id === reportId
                ? {
                    ...report,
                    userAssignmentStatus: userAssignmentStatus ?? newStatus,
                  }
                : report,
            ),
          );
        }

        setReportUsers((prev) => ({
          ...prev,
          [reportId]: updatedUsers,
        }));
      } catch (error) {
        console.error('Error updating user report access:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      reports,
      reportUsers,
      userId,
      clientId,
      loading,
      setLoading,
      setReports,
      setReportUsers,
    ],
  );

  return {
    handleReportAccessChange,
    handleDataIssueChange,
    handleUserAccessChange,
  };
};

export default useAccessActions;
